/**
 * AlphaProfile — first-party browsing profile ("pixel") for AlphaPets.
 *
 * Records what the shopper looks at, then derives an affinity profile that
 * smart-search.js uses to personalise ranking and the zero state.
 *
 * Design constraints:
 * - First-party localStorage only. Nothing leaves the device, no PII, no
 *   third-party calls, no cookies.
 * - Never blocks or breaks the page: every entry point is wrapped, and a
 *   corrupt/absent store degrades to "no personalisation".
 * - Stays compatible with the theme's existing `cc-recently-viewed` list
 *   (see layout/theme.liquid + sections/recently-viewed.liquid), which is kept
 *   in sync so that section keeps working unchanged.
 */
(function (window) {
  'use strict';

  var KEY = 'ap-browsing-profile';
  var LEGACY_KEY = 'cc-recently-viewed';
  var VERSION = 1;

  var MAX_EVENTS = 140;
  var MAX_RECENT = 12;
  var MAX_SEARCHES = 8;

  /** Event weights — an add-to-cart says far more than a page view. */
  var WEIGHTS = { view: 1, search: 1.5, cart: 4 };

  /** Interest half-life in days: a 14-day-old view counts half as much. */
  var HALF_LIFE_DAYS = 14;
  var DAY = 86400000;

  /* ------------------------------------------------------------------ store */

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!raw || raw.v !== VERSION) return blank();
      if (!Array.isArray(raw.events) || !Array.isArray(raw.recent)) return blank();
      return raw;
    } catch (e) {
      return blank();
    }
  }

  function blank() {
    return { v: VERSION, events: [], recent: [], searches: [] };
  }

  function write(data) {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
      // Quota or private mode — drop the oldest half and try once more.
      try {
        data.events = data.events.slice(0, Math.floor(MAX_EVENTS / 2));
        localStorage.setItem(KEY, JSON.stringify(data));
      } catch (e2) { /* give up silently; personalisation is optional */ }
    }
  }

  /** Keep the theme's existing recently-viewed list in sync. */
  function syncLegacy(id) {
    if (!id) return;
    try {
      var items = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
      if (!Array.isArray(items)) items = [];
      items = items.filter(function (x) { return x !== id; });
      items.unshift(id);
      localStorage.setItem(LEGACY_KEY, JSON.stringify(items.slice(0, MAX_RECENT)));
    } catch (e) { /* non-fatal */ }
  }

  /* ------------------------------------------------------------- inference */

  var CAT = /貓|喵|cat/i;
  var DOG = /狗|犬|汪|dog/i;

  function speciesOf(text) {
    var s = String(text || '');
    var c = CAT.test(s);
    var d = DOG.test(s);
    if (c && !d) return 'cat';
    if (d && !c) return 'dog';
    return 'both';
  }

  function bandOf(price) {
    var p = Number(price) || 0;
    if (p < 100) return 'b0';
    if (p < 300) return 'b1';
    if (p < 600) return 'b2';
    if (p < 1000) return 'b3';
    return 'b4';
  }

  /* --------------------------------------------------------------- tracking */

  function push(kind, payload) {
    var data = read();
    var ev = {
      t: Date.now(),
      k: kind,
      id: payload.id || null,
      ty: payload.type || '',
      ve: payload.vendor || '',
      sp: payload.species || speciesOf((payload.title || '') + ' ' + (payload.type || '')),
      bd: payload.price == null ? '' : bandOf(payload.price)
    };
    data.events.unshift(ev);
    data.events = data.events.slice(0, MAX_EVENTS);
    write(data);
    return data;
  }

  var API = {
    /** Called on product pages. `p` mirrors the Liquid product object. */
    trackProduct: function (p) {
      if (!p || !p.id) return;
      var data = push('view', p);
      data.recent = data.recent.filter(function (x) { return x.id !== p.id; });
      data.recent.unshift({
        id: p.id,
        handle: p.handle || '',
        title: p.title || '',
        url: p.url || '',
        img: p.img || '',
        type: p.type || '',
        vendor: p.vendor || '',
        price: p.price == null ? null : Number(p.price),
        t: Date.now()
      });
      data.recent = data.recent.slice(0, MAX_RECENT);
      write(data);
      syncLegacy(p.id);
    },

    /** Called on collection pages — weaker signal, no product id. */
    trackCollection: function (c) {
      if (!c || !c.title) return;
      push('view', { type: c.type || '', vendor: '', title: c.title, price: null });
    },

    /** Called when a search is submitted, and when a result is opened. */
    trackSearch: function (terms) {
      var q = String(terms || '').trim();
      if (!q) return;
      var data = read();
      data.searches = (data.searches || []).filter(function (x) { return x !== q; });
      data.searches.unshift(q);
      data.searches = data.searches.slice(0, MAX_SEARCHES);
      write(data);
      push('search', { title: q, price: null });
    },

    trackCartAdd: function (p) {
      if (!p) return;
      push('cart', p);
    },

    recent: function () { return read().recent || []; },
    recentIds: function () {
      return (read().recent || []).map(function (r) { return r.id; });
    },
    searches: function () { return read().searches || []; },

    /**
     * Aggregate the decayed event log into an affinity profile.
     * Returns normalised weights in 0..1 per dimension.
     */
    affinity: function () {
      var data = read();
      var now = Date.now();
      var species = { cat: 0, dog: 0 };
      var types = {}, vendors = {}, bands = {};
      var total = 0;

      data.events.forEach(function (e) {
        var age = (now - e.t) / DAY;
        var w = (WEIGHTS[e.k] || 1) * Math.pow(0.5, age / HALF_LIFE_DAYS);
        if (w < 0.01) return;
        total += w;
        if (e.sp === 'cat' || e.sp === 'dog') species[e.sp] += w;
        if (e.ty) types[e.ty] = (types[e.ty] || 0) + w;
        if (e.ve) vendors[e.ve] = (vendors[e.ve] || 0) + w;
        if (e.bd) bands[e.bd] = (bands[e.bd] || 0) + w;
      });

      var norm = function (o) {
        var max = 0;
        Object.keys(o).forEach(function (k) { if (o[k] > max) max = o[k]; });
        if (!max) return {};
        var out = {};
        Object.keys(o).forEach(function (k) { out[k] = o[k] / max; });
        return out;
      };

      var spTotal = species.cat + species.dog;
      return {
        // How much evidence we have at all. Below ~3 we stay hands-off.
        confidence: Math.min(1, total / 8),
        events: data.events.length,
        species: spTotal ? { cat: species.cat / spTotal, dog: species.dog / spTotal } : null,
        leadSpecies: !spTotal ? null : (species.cat > species.dog * 1.3 ? 'cat'
          : species.dog > species.cat * 1.3 ? 'dog' : null),
        types: norm(types),
        vendors: norm(vendors),
        bands: norm(bands)
      };
    },

    /**
     * Affinity score in 0..1 for one candidate item.
     * `item` = { species, type, vendor, price }
     */
    score: function (item, aff) {
      aff = aff || API.affinity();
      if (!aff.confidence) return 0;
      var s = 0;
      if (aff.species && item.species && item.species !== 'both') {
        s += 0.40 * (aff.species[item.species] || 0);
      }
      if (item.type && aff.types[item.type]) s += 0.30 * aff.types[item.type];
      if (item.vendor && aff.vendors[item.vendor]) s += 0.20 * aff.vendors[item.vendor];
      if (item.price != null && aff.bands[bandOf(item.price)]) {
        s += 0.10 * aff.bands[bandOf(item.price)];
      }
      return s * aff.confidence;
    },

    /** Human-readable reason, for the "為你推薦" label. */
    reason: function (item, aff) {
      aff = aff || API.affinity();
      if (!aff.confidence) return '';
      if (item.vendor && (aff.vendors[item.vendor] || 0) > 0.6) return '你常看 ' + item.vendor;
      if (item.type && (aff.types[item.type] || 0) > 0.6) return '你常看' + item.type;
      if (aff.leadSpecies && item.species === aff.leadSpecies) {
        return aff.leadSpecies === 'cat' ? '貓咪相關' : '狗狗相關';
      }
      return '';
    },

    speciesOf: speciesOf,
    bandOf: bandOf,

    reset: function () {
      try { localStorage.removeItem(KEY); } catch (e) {}
    },

    /** Debug helper: AlphaProfile.debug() in the console. */
    debug: function () { return { store: read(), affinity: API.affinity() }; }
  };

  window.AlphaProfile = API;

  // Drain anything queued before this script finished loading.
  var q = window.AlphaProfileQueue;
  if (q && q.length) {
    q.forEach(function (job) {
      try {
        if (API[job[0]]) API[job[0]].apply(API, job.slice(1));
      } catch (e) { /* non-fatal */ }
    });
  }
  window.AlphaProfileQueue = { push: function (job) { try { API[job[0]].apply(API, job.slice(1)); } catch (e) {} } };
})(window);
